import type { ClassLessonAlarmOffset, ClassLessonAlarmSoundKind } from './classLessonAlarmSettings';
import { CLASS_LESSON_ALARM_OFFSET_OPTIONS } from './classLessonAlarmSettings';
import type { ClassLessonAlarmTarget } from './classLessonAlarmSchedule';

export type ClassLessonAlarmScheduleMode = 'auto' | 'custom';

export type ClassLessonAlarmCustomBlockOverride = {
  time: string;
  offset: ClassLessonAlarmOffset;
  channel: 'regular' | 'dedicated';
  soundKind: ClassLessonAlarmSoundKind;
  /** 블럭 활성(울림) 여부 */
  enabled: boolean;
};

export type ClassLessonAlarmCustomSchedule = {
  v: 1;
  mode: ClassLessonAlarmScheduleMode;
  /** 키: `${gradeId}|${slotIndex}` */
  overrides: Record<string, ClassLessonAlarmCustomBlockOverride>;
};

export const CLASS_LESSON_ALARM_CUSTOM_SCHEDULE_CHANGED_EVENT =
  'cp:class-lesson-alarm-custom-schedule-changed';

export type ClassLessonAlarmCustomScheduleChangedDetail = {
  workspaceId: string;
};

export function classLessonAlarmBlockKey(gradeId: string, slotIndex: number): string {
  return `${gradeId.trim()}|${slotIndex}`;
}

export function createDefaultClassLessonAlarmCustomSchedule(): ClassLessonAlarmCustomSchedule {
  return { v: 1, mode: 'auto', overrides: {} };
}

function normalizeHHMM(raw: string): string | null {
  const value = (raw ?? '').trim();
  const m = /^(\d{1,2})\s*:\s*([0-5]\d)$/.exec(value);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || h < 0 || h > 23) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

function normalizeOffset(raw: unknown): ClassLessonAlarmOffset | null {
  if (raw === '1M' || raw === '3M' || raw === '5M') return raw;
  return null;
}

function normalizeChannel(raw: unknown): 'regular' | 'dedicated' {
  return raw === 'dedicated' ? 'dedicated' : 'regular';
}

function normalizeSoundKind(raw: unknown): ClassLessonAlarmSoundKind {
  return raw === 'activity' ? 'activity' : 'basic';
}

function overrideFromPartial(
  row: Record<string, unknown>,
  fallback?: Partial<ClassLessonAlarmCustomBlockOverride>
): ClassLessonAlarmCustomBlockOverride | null {
  const time = normalizeHHMM(String(row.time ?? fallback?.time ?? ''));
  const offset = normalizeOffset(row.offset) ?? fallback?.offset ?? null;
  if (!time || !offset) return null;
  return {
    time,
    offset,
    channel: normalizeChannel(row.channel ?? fallback?.channel),
    soundKind: normalizeSoundKind(row.soundKind ?? fallback?.soundKind),
    enabled: typeof row.enabled === 'boolean' ? row.enabled : fallback?.enabled !== false,
  };
}

export function normalizeClassLessonAlarmCustomSchedule(
  raw: unknown
): ClassLessonAlarmCustomSchedule {
  const base = createDefaultClassLessonAlarmCustomSchedule();
  if (!raw || typeof raw !== 'object') return base;
  const o = raw as Record<string, unknown>;
  const mode: ClassLessonAlarmScheduleMode = o.mode === 'custom' ? 'custom' : 'auto';
  const overrides: Record<string, ClassLessonAlarmCustomBlockOverride> = {};
  const src =
    o.overrides && typeof o.overrides === 'object'
      ? (o.overrides as Record<string, unknown>)
      : {};
  for (const [key, value] of Object.entries(src)) {
    const blockKey = key.trim();
    if (!blockKey || !value || typeof value !== 'object') continue;
    const parsed = overrideFromPartial(value as Record<string, unknown>);
    if (!parsed) continue;
    overrides[blockKey] = parsed;
  }
  return { v: 1, mode, overrides };
}

function fallbackOverrideFromTarget(t: ClassLessonAlarmTarget): ClassLessonAlarmCustomBlockOverride {
  return {
    time: t.time,
    offset: t.offset,
    channel: t.channel,
    soundKind: t.soundKind,
    /** 커스텀 기본·초기화: 항상 활성 */
    enabled: true,
  };
}

/** 자동 타깃에 커스텀 오버라이드 적용 (mode=auto면 원본 그대로) */
export function applyClassLessonAlarmCustomSchedule(
  targets: ClassLessonAlarmTarget[],
  custom: ClassLessonAlarmCustomSchedule | null | undefined
): ClassLessonAlarmTarget[] {
  if (!custom || custom.mode !== 'custom') return targets;
  const next = targets.map((t) => {
    const key = classLessonAlarmBlockKey(t.gradeId, t.slotIndex);
    const ov = custom.overrides[key] ?? fallbackOverrideFromTarget(t);
    return {
      ...t,
      time: ov.time,
      offset: ov.offset,
      channel: ov.channel,
      soundKind: ov.soundKind,
      /** 커스텀: 미편성 없음 */
      hasLesson: true,
      willRing: ov.enabled,
    };
  });
  return next.sort((a, b) => {
    const byTime = a.time.localeCompare(b.time);
    if (byTime !== 0) return byTime;
    const byGrade = a.gradeLabel.localeCompare(b.gradeLabel, 'ko');
    if (byGrade !== 0) return byGrade;
    return a.slotIndex - b.slotIndex;
  });
}

/** 현재 자동 스케줄을 커스텀 오버라이드 시드로 복사 */
export function seedCustomOverridesFromTargets(
  targets: ClassLessonAlarmTarget[]
): Record<string, ClassLessonAlarmCustomBlockOverride> {
  const overrides: Record<string, ClassLessonAlarmCustomBlockOverride> = {};
  for (const t of targets) {
    overrides[classLessonAlarmBlockKey(t.gradeId, t.slotIndex)] = fallbackOverrideFromTarget(t);
  }
  return overrides;
}

export function cycleClassLessonAlarmOffset(current: ClassLessonAlarmOffset): ClassLessonAlarmOffset {
  const idx = CLASS_LESSON_ALARM_OFFSET_OPTIONS.findIndex((o) => o.value === current);
  const next = CLASS_LESSON_ALARM_OFFSET_OPTIONS[(idx + 1) % CLASS_LESSON_ALARM_OFFSET_OPTIONS.length];
  return next?.value ?? '3M';
}

export function emitClassLessonAlarmCustomScheduleChanged(workspaceId: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(CLASS_LESSON_ALARM_CUSTOM_SCHEDULE_CHANGED_EVENT, {
      detail: { workspaceId } satisfies ClassLessonAlarmCustomScheduleChangedDetail,
    })
  );
}
